// ─────────────────────────────────────────────────────────────────────────────
// «عنوان دستور» as a searchable catalogue picker, inside the قالب دستورات editor.
//
// The visit's «افزودن» tab picks orders from the clinic's service catalogue and
// keeps the catalogue id on the row. A template written by typing names carried
// none of that: no id, no کد, and no way for anything downstream to tell that
// «سونوگرافی داپلر» and «سونو داپلر» are one service. This gives the template
// editor the same door the visit has — type a few letters, pick a row, and the
// item arrives with its catalogue id and its code already filled in.
//
// It is a COMBOBOX, not a closed list: the input stays free text. A clinic
// orders things the national catalogue has never heard of, and refusing to save
// what a doctor typed would be worse than saving it without a code.
//
// Deliberately small, like TemplateFormularySearch beside it: one box, one short
// list. The visit's full picker (type rail, in-house floating, batch add) is
// built for ordering in a hurry; a template is written once, calmly.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useRef, useState } from 'react';
import { C, soft } from '@parsina/framework/tokens';
import { TextInput } from '@parsina/framework';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { searchOrderCatalog } from '@parsina/framework/clinical/src/screens/Visit/Visit.shared';
import { ORDER_TYPE_KEYS } from '@parsina/framework/clinical/src/constants/patientRecord';
import { R_CONTROL } from './SettingsKit';

/**
 * Search the service catalogue, one request per pause in typing.
 *
 * This used to fetch the WHOLE catalogue once per open editor and filter it in
 * the browser. That is exactly what stops working when the national مکسا list is
 * in the table (~20,000 rows), so the editor asks the same question the visit's
 * picker asks: a page of matches, ranked by the server, with what this clinic
 * performs first.
 *
 * `appointmentId` is deliberately absent — Settings has no visit, so no clinic
 * scoping and no inHouse flag. A template is written once and applied at
 * whichever clinic the doctor is working at that day.
 */
export function useOrderCatalogSearch({ query, orderType, enabled = true }) {
  const { t } = useTranslation();
  const [state, setState] = useState({ items: [], total: 0, loading: false, error: null });

  useEffect(() => {
    if (!enabled) return undefined;
    const q = (query || '').trim();
    if (!q) { setState({ items: [], total: 0, loading: false, error: null }); return undefined; }
    let live = true;
    setState((prev) => ({ ...prev, loading: true }));
    const timer = setTimeout(() => {
      searchOrderCatalog({ query: q, orderType: orderType || '' })
        .then((page) => {
          if (!live) return;
          setState({
            items: (page.entries || []).map((e) => ({
              catalogId: e.id,
              orderType: e.orderType,
              name: e.name,
              nameEn: e.nameEn || '',
              code: e.code || '',
              codeSystem: e.codeSystem || '',
              sub: e.code ? `${e.codeSystem || ''} ${e.code}`.trim() : ([e.modality, e.bodyPart].filter(Boolean).join(' · ')),
              category: e.category || '',
            })),
            total: page.total || 0, loading: false, error: null,
          });
        })
        .catch((err) => {
          if (!live) return;
          // Said out loud, never swallowed: the field stays free text, so the
          // doctor can type the name — but «no results» and «the search failed»
          // must not look the same.
          setState({ items: [], total: 0, loading: false, error: t('settings.templates.loadFailed') });
        });
    }, 250);
    return () => { live = false; clearTimeout(timer); };
  }, [query, orderType, enabled, t]);

  return state;
}

const norm = (s) => String(s || '').toLowerCase().trim();

/**
 * @param value       the row's current `name`
 * @param onChange    free typing — the name, with no catalogue behind it
 * @param onPick      (item) => void — sets name, code, orderType and catalogId together
 * @param orderType   the row's type; matching services are offered first
 * @param catalogId   set once a row has been picked — the helper line then says so
 * @param bare        inline inside a list line: no visible label, no helper text
 */
export default function TemplateOrderPicker({
  value, onChange, onPick, orderType, error, label, required = true, catalogId = null,
  bare = false,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);

  // `error` is already the field's validation message; the search's failure is
  // a different thing and must not overwrite it.
  const { items, loading, error: searchError } = useOrderCatalogSearch({
    // Deliberately NOT filtered by the row's type. A doctor editing a line
    // labelled «آزمایش» who types «آسپیراسیون» should still find the procedure —
    // picking it corrects the type. Filtering server-side would hide it, and the
    // doctor would have no way to know the service exists.
    query: value, orderType: '', enabled: open,
  });

  // The server ranks in-house first and already filtered by query and type; the
  // row's OWN type floats to the front of what came back, because a doctor
  // editing a lab line is most likely still choosing a lab.
  const matches = useMemo(() => items
    .map((it, i) => [it, i])
    .sort((a, b) => ((b[0].orderType === orderType) - (a[0].orderType === orderType)) || (a[1] - b[1]))
    .map(([it]) => it), [items, orderType]);

  // A click anywhere else closes the list. Pointerdown, not click: the input's
  // blur must not race the option's own mousedown handler.
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [open]);

  useEffect(() => { setActive(0); }, [value]);

  const choose = (item) => {
    onPick(item);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open || matches.length === 0) {
      if (e.key === 'ArrowDown' && matches.length) setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => (i + 1) % matches.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => (i - 1 + matches.length) % matches.length); }
    else if (e.key === 'Enter') {
      // Only when the list is genuinely being navigated — Enter on a typed name
      // the doctor never opened the list for must not silently swap in a service.
      e.preventDefault();
      choose(matches[active]);
    } else if (e.key === 'Escape') { setOpen(false); }
  };

  const showList = open && norm(value).length > 0;

  return (
    <div ref={boxRef} style={{ position: 'relative' }} data-testid="template-order-picker">
      <TextInput
        label={bare ? undefined : (label || t('settings.templates.order_.name'))}
        aria-label={bare ? t('settings.templates.order_.name') : undefined}
        placeholder={bare ? t('settings.templates.orderSearchPlaceholder') : undefined}
        required={bare ? false : required}
        value={value}
        onChange={(e) => { onChange(e); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        error={error}
        helperText={
          bare || searchError ? undefined
            : catalogId ? t('settings.templates.orderCatalogLinked')
            : t('settings.templates.orderCatalogHint')
        }
        autoComplete="off"
        role="combobox"
        aria-expanded={showList}
        aria-autocomplete="list"
        data-testid="template-item-name"
      />

      {showList && (
        <div
          role="listbox"
          data-testid="template-order-options"
          style={{
            position: 'absolute', zIndex: 40, insetInlineStart: 0, insetInlineEnd: 0, top: '100%',
            marginTop: 2, maxHeight: 260, overflowY: 'auto',
            border: `1px solid ${C.border}`, borderRadius: R_CONTROL, background: C.card,
            boxShadow: '0 10px 24px rgba(0,0,0,0.10)', padding: 4,
          }}
        >
          {loading ? (
            <div style={{ padding: '8px 10px', fontSize: 12, color: C.textMuted }}>
              {t('common.state.loading')}
            </div>
          ) : searchError ? (
            <div role="alert" data-testid="template-order-error" style={{ padding: '8px 10px', fontSize: 12, color: C.red }}>
              {t('settings.templates.orderCatalogFailed')}
            </div>
          ) : matches.length === 0 ? (
            <div data-testid="template-order-empty" style={{ padding: '8px 10px', fontSize: 12, color: C.textMuted }}>
              {t('settings.templates.orderCatalogEmpty')}
            </div>
          ) : (
            matches.map((item, i) => (
              <div
                key={`${item.orderType}:${item.catalogId || item.name}`}
                role="option"
                aria-selected={i === active}
                data-testid="template-order-option"
                data-catalog-id={item.catalogId || ''}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => { e.preventDefault(); choose(item); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                  borderRadius: 8, cursor: 'pointer',
                  background: i === active ? soft(C.navy, 0.07) : 'transparent',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div dir="auto" style={{ fontSize: 12.5, fontWeight: 700, color: C.textPrimary }}>{item.name}</div>
                  <div style={{ fontSize: 10.5, color: C.textMuted, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {item.sub && <span dir="auto">{item.sub}</span>}
                    {item.category && <span>· {item.category}</span>}
                  </div>
                </div>
                {item.orderType !== orderType && (
                  <span style={{ fontSize: 10, color: C.textSecondary, flexShrink: 0 }}>
                    {t(ORDER_TYPE_KEYS[item.orderType] || item.orderType)}
                  </span>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
